import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import {
  useAddressAutocomplete,
  type AddressComponents,
} from '../../hooks/useAddressAutocomplete';
import { useKyc } from '../../hooks/useKyc';
import { routes } from '@/routes/routes';
import styles from './ManualAddressPage.module.scss';

const LOCALITY_TYPE_OPTIONS = ['Municipiu', 'Oraș', 'Comună', 'Sat'];

const emptyComponents: AddressComponents = {
  county: '',
  locality: '',
  localityType: '',
  street: '',
  streetNumber: '',
  building: '',
  entrance: '',
  floor: '',
  apartment: '',
  sector: '',
};

function ManualAddressPage() {
  const navigate = useNavigate();
  const { predictions, fetchPredictions, clearPredictions, getAddressComponents } =
    useAddressAutocomplete();
  const { updateAddress, isAddressUpdating, kycState } = useKyc(true);

  useEffect(() => {
    if (kycState && !kycState.requiresManualAddress) {
      navigate(routes.kyc.base);
    }
  }, [kycState]);

  const [inputValue, setInputValue] = useState('');
  const [addressSelected, setAddressSelected] = useState(false);
  const [fields, setFields] = useState<AddressComponents>(emptyComponents);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setAddressSelected(false);
    setFields(emptyComponents);
    fetchPredictions(value);
  };

  const handleSelect = async (placeId: string) => {
    setInputValue('');
    clearPredictions();

    try {
      const components = await getAddressComponents(placeId);
      setFields(components);
      setAddressSelected(true);
    } catch {
      setAddressSelected(true);
    }
  };

  const handleFieldChange =
    (field: keyof AddressComponents) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setFields((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const isValid =
    addressSelected &&
    !!fields.county.trim() &&
    !!fields.locality.trim() &&
    !!fields.localityType &&
    !!fields.street.trim();

  const handleContinue = async () => {
    const updatedState = await updateAddress({
      countyName: fields.county,
      localityName: fields.locality,
      localityType: fields.localityType,
      street: fields.street,
      streetNumber: fields.streetNumber,
      entrance: fields.entrance,
      floor: fields.floor,
      apartment: fields.apartment,
      sector: fields.sector,
      building: fields.building,
    });

    if (updatedState.requiresProofOfAddress) {
      navigate(routes.kyc.proofOfAddress);
    } else if (updatedState.hasUsableAddress) {
      navigate(routes.wizard.base);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <span className={styles.emoji} role="img" aria-label="casă">
            🏠
          </span>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Adresa de reședință</h1>
            <p className={styles.subtitle}>
              Te rugăm să introduci adresa exact așa cum apare în cartea de
              identitate.
            </p>
          </div>
        </div>

        <div className={styles.fields}>
          <div className={styles.searchWrapper}>
            <div className={styles.searchField}>
              <span className={styles.countryFlag} role="img" aria-label="România">
                🇷🇴
              </span>
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Caută adresa ta..."
                value={inputValue}
                onChange={handleInput}
                autoComplete="off"
              />
            </div>

            {predictions.length > 0 && (
              <ul className={styles.suggestions}>
                {predictions.map(({ placeId, description }) => (
                  <li
                    key={placeId}
                    className={styles.suggestionItem}
                    onClick={() => handleSelect(placeId)}
                  >
                    {description}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {addressSelected && (
            <>
              <div className={styles.addressField}>
                <label className={styles.fieldLabel}>Județ</label>
                <input
                  className={styles.fieldInput}
                  value={fields.county}
                  readOnly
                  disabled
                />
              </div>

              <div className={styles.row}>
                <div className={styles.addressField}>
                  <label className={styles.fieldLabel}>Localitate</label>
                  <input
                    className={styles.fieldInput}
                    value={fields.locality}
                    readOnly
                    disabled
                  />
                </div>
                <div className={styles.tipField}>
                  <label className={styles.fieldLabel}>Tip</label>
                  <select
                    className={styles.fieldSelect}
                    value={fields.localityType}
                    onChange={handleFieldChange('localityType')}
                  >
                    <option value="" disabled />
                    {LOCALITY_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.addressField}>
                <label className={styles.fieldLabel}>Strada</label>
                <input
                  className={styles.fieldInput}
                  value={fields.street}
                  onChange={handleFieldChange('street')}
                />
              </div>

              <div className={styles.row}>
                <div className={styles.numarField}>
                  <label className={styles.fieldLabel}>Număr</label>
                  <input
                    className={styles.fieldInput}
                    value={fields.streetNumber}
                    onChange={handleFieldChange('streetNumber')}
                  />
                </div>
                <div className={styles.addressField}>
                  <label className={styles.fieldLabel}>Bloc</label>
                  <input
                    className={styles.fieldInput}
                    value={fields.building}
                    onChange={handleFieldChange('building')}
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.addressField}>
                  <label className={styles.fieldLabel}>Scară</label>
                  <input
                    className={styles.fieldInput}
                    value={fields.entrance}
                    onChange={handleFieldChange('entrance')}
                  />
                </div>
                <div className={styles.addressField}>
                  <label className={styles.fieldLabel}>Etaj</label>
                  <input
                    className={styles.fieldInput}
                    value={fields.floor}
                    onChange={handleFieldChange('floor')}
                  />
                </div>
                <div className={styles.addressField}>
                  <label className={styles.fieldLabel}>Apartament</label>
                  <input
                    className={styles.fieldInput}
                    value={fields.apartment}
                    onChange={handleFieldChange('apartment')}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.bottomSection}>
        <Button
          trackingId="submit_manual_address"
          variant="filled"
          text="Continuă"
          disabled={!isValid || isAddressUpdating}
          loading={isAddressUpdating}
          rightIcon={<Icon name="arrow-right" size={16} />}
          className={styles.continueButton}
          onClick={handleContinue}
        />
      </div>
    </div>
  );
}

export default ManualAddressPage;
